import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, PackageCheck, Wrench, TerminalSquare, Monitor, Cpu } from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  ["overview", "Overview", Cpu],
  ["compliance", "Compliance", ShieldCheck],
  ["patches", "Patches", PackageCheck],
  ["terminal", "Terminal", TerminalSquare],
  ["screen", "Remote Desktop", Monitor],
];

export default function AgentDetail() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try { const { data } = await api.get(`/agent/${id}`); setAgent(data); }
    catch { toast.error("Agent not found"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/agents" className="text-zinc-500 hover:text-zinc-200"><ArrowLeft size={18} /></Link>
        <h1 className="font-display text-2xl font-semibold tracking-tight">{agent?.hostname || id}</h1>
        {agent && <StatusBadge status={agent.online ? "online" : "offline"} />}
        <span className="ml-auto font-mono text-xs text-zinc-600">{id}</span>
      </div>

      <div className="flex gap-1 border-b border-zinc-900">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={"flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px " +
              (tab === key ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200")}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview agent={agent} />}
      {tab === "compliance" && <Compliance id={id} online={agent?.online} />}
      {tab === "patches" && <Patches id={id} online={agent?.online} />}
      {tab === "terminal" && <Terminal id={id} online={agent?.online} />}
      {tab === "screen" && <Screen id={id} online={agent?.online} />}
    </div>
  );
}

function Card({ title, children, right }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
        <div className="font-display font-medium text-sm">{title}</div>{right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ k, v }) {
  return <div className="flex justify-between py-1.5 border-b border-zinc-900/60 text-sm">
    <span className="text-zinc-500">{k}</span><span className="font-mono text-zinc-200">{v ?? "—"}</span></div>;
}

function Overview({ agent }) {
  const m = agent?.metrics || {};
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Card title="System">
        <Field k="Hostname" v={agent?.hostname} />
        <Field k="OS" v={`${agent?.os || ""} ${agent?.os_version || ""}`} />
        <Field k="Serial" v={agent?.serial_number} />
        <Field k="Vendor" v={[agent?.manufacturer, agent?.model].filter(Boolean).join(" ")} />
        <Field k="Agent version" v={agent?.version} />
        <Field k="Last heartbeat" v={agent?.last_heartbeat?.slice(11, 19)} />
      </Card>
      <Card title="Live metrics">
        {["cpu", "mem", "disk"].map((key) => (
          <div key={key} className="mb-3">
            <div className="flex justify-between text-sm mb-1"><span className="text-zinc-500 uppercase">{key}</span><span className="font-mono">{m[key] ?? "—"}%</span></div>
            <div className="h-1.5 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, m[key] || 0)}%`, background: (m[key] || 0) >= 90 ? "#ef4444" : (m[key] || 0) >= 75 ? "#f59e0b" : "#10b981" }} /></div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function sevColor(s) {
  return { critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-emerald-400" }[s] || "text-zinc-400";
}

function Compliance({ id, online }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get(`/agent/${id}/compliance`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { setBusy(true); try { await api.post(`/agent/${id}/compliance/scan`); toast.success("Compliance scan requested"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const remediate = async (controlId) => { try { const r = await api.post(`/agent/${id}/remediate?control_id=${encodeURIComponent(controlId)}`); toast.success(`Remediation ${r.data.delivered ? "dispatched" : "queued"}`); } catch (e) { toast.error(e.response?.data?.detail || "No remediation available"); } };

  const controls = data?.controls || [];
  return (
    <Card title={`Compliance audit ${data?.score != null ? `· ${data.score}%` : ""}`}
      right={<button onClick={scan} disabled={busy || !online} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Run scan</button>}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">Control</th><th className="text-left">Title</th><th className="text-left">Severity</th><th className="text-left">Status</th><th></th></tr></thead>
        <tbody>
          {controls.map((c) => (
            <tr key={c.control_id} className="border-t border-zinc-900/60">
              <td className="py-2 font-mono text-xs">{c.control_id}</td>
              <td className="text-zinc-300">{c.title}</td>
              <td className={"text-xs " + sevColor(c.severity)}>{c.severity}</td>
              <td className={"text-xs font-medium " + (c.status === "pass" ? "text-emerald-400" : c.status === "fail" ? "text-red-400" : "text-zinc-500")}>{c.status}</td>
              <td className="text-right">{c.status === "fail" && <button onClick={() => remediate(c.control_id)} className="flex items-center gap-1 ml-auto text-xs text-blue-400 hover:text-blue-300"><Wrench size={12} />Remediate</button>}</td>
            </tr>
          ))}
          {controls.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No compliance data — run a scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function Patches({ id, online }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [kb, setKb] = useState("");
  const load = async () => { const { data } = await api.get(`/agent/${id}/patches`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { try { await api.post(`/agent/${id}/patch/scan`); toast.success("Patch scan requested (~30s)"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const install = async () => { const ids = Object.keys(sel).filter((k) => sel[k]); if (!ids.length) return; try { const r = await api.post(`/agent/${id}/patch/install`, { update_ids: ids, reboot_allowed: false }); toast.success(`Install job ${r.data.job_id}`); setSel({}); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const rollback = async () => { if (!kb) return; try { const r = await api.post(`/agent/${id}/patch/uninstall`, { kb }); toast.success(`Rollback job ${r.data.job_id}`); setKb(""); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const updates = data?.updates || [];
  return (
    <Card title={`Available updates (${updates.length})`}
      right={<div className="flex items-center gap-2">
        <input value={kb} onChange={(e) => setKb(e.target.value)} placeholder="KB5030213" className="w-28 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs font-mono" />
        <button onClick={rollback} disabled={!kb || !online} className="bg-zinc-800 disabled:opacity-40 text-zinc-200 text-xs rounded px-3 py-1.5">Rollback</button>
        <button onClick={scan} disabled={!online} className="bg-zinc-800 text-zinc-200 text-xs rounded px-3 py-1.5">Scan</button>
        <button onClick={install} disabled={!Object.values(sel).some(Boolean)} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Install selected</button>
      </div>}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th></th><th className="text-left py-2">KB</th><th className="text-left">Severity</th><th className="text-left">Size</th><th className="text-left">Title</th></tr></thead>
        <tbody>
          {updates.map((u) => (
            <tr key={u.update_id} className="border-t border-zinc-900/60">
              <td className="py-2"><input type="checkbox" checked={!!sel[u.update_id]} onChange={(e) => setSel({ ...sel, [u.update_id]: e.target.checked })} /></td>
              <td className="font-mono text-xs">{u.kb || u.update_id.slice(0, 8)}</td>
              <td className="text-xs text-zinc-400">{u.severity || "—"}</td>
              <td className="font-mono text-xs">{u.size_mb} MB</td>
              <td className="text-zinc-300">{u.title}</td>
            </tr>
          ))}
          {updates.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No updates — run a scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function wsUrl(path) {
  const base = (BACKEND || window.location.origin).replace(/^http/, "ws");
  return base + "/api" + path;
}
async function ticket() { const { data } = await api.post("/agent/ws-ticket"); return data.ticket; }

function Terminal({ id, online }) {
  const [lines, setLines] = useState("");
  const [cmd, setCmd] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const outRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    (async () => {
      const t = await ticket();
      ws = new WebSocket(wsUrl(`/agent/${id}/terminal?shell=cmd&token=${encodeURIComponent(t)}`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => setLines((p) => (p + e.data).slice(-60000));
    })();
    return () => ws && ws.close();
  }, [id, online]);
  useEffect(() => { if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [lines]);

  const send = (e) => { e.preventDefault(); if (wsRef.current?.readyState === 1) { wsRef.current.send(cmd + "\r\n"); setCmd(""); } };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — terminal unavailable.</div>;

  return (
    <div className="bg-black border border-zinc-900 rounded-md overflow-hidden h-[460px] flex flex-col">
      <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2 text-xs"><span className={connected ? "text-emerald-400" : "text-zinc-500"}>● {connected ? "connected" : "connecting…"}</span></div>
      <pre ref={outRef} className="flex-1 overflow-y-auto p-3 font-mono text-[12.5px] whitespace-pre-wrap text-zinc-300">{lines || "…"}</pre>
      <form onSubmit={send} className="flex border-t border-zinc-900"><span className="px-2 py-2 text-emerald-400 font-mono">›</span><input value={cmd} onChange={(e) => setCmd(e.target.value)} autoFocus className="flex-1 bg-black text-zinc-200 font-mono text-sm px-1 outline-none" placeholder="type a command…" /></form>
    </div>
  );
}

function Screen({ id, online }) {
  const [frame, setFrame] = useState(null);
  const [control, setControl] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    (async () => {
      const t = await ticket();
      ws = new WebSocket(wsUrl(`/agent/${id}/screen?token=${encodeURIComponent(t)}&interval_ms=500&quality=55`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => { const m = JSON.parse(e.data); if (m.image_b64) setFrame({ src: `data:image/jpeg;base64,${m.image_b64}`, w: m.width, h: m.height, seq: m.seq }); };
    })();
    return () => ws && ws.close();
  }, [id, online]);

  const coords = (e) => { const r = imgRef.current.getBoundingClientRect(); return { x: Math.round((e.clientX - r.left) / r.width * frame.w), y: Math.round((e.clientY - r.top) / r.height * frame.h) }; };
  const send = (ev) => { if (control && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(ev)); };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — remote desktop unavailable.</div>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-sm">
        <span className={connected ? "text-emerald-400" : "text-zinc-500"}>● {connected ? "streaming" : "connecting…"}</span>
        <label className="flex items-center gap-2 text-zinc-300"><input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} />Take control</label>
        <span className="ml-auto font-mono text-xs text-zinc-600">{frame ? `${frame.w}×${frame.h} #${frame.seq}` : "no frame"}</span>
      </div>
      <div tabIndex={0} onKeyDown={(e) => { if (!control) return; e.preventDefault(); e.key.length === 1 ? send({ kind: "text", text: e.key }) : send({ kind: "key", key: e.key }); }}
        className="bg-black border border-zinc-900 rounded-md overflow-hidden" style={{ outline: control ? "2px solid #2563eb" : "none" }}>
        {frame ? <img ref={imgRef} src={frame.src} alt="remote" draggable={false}
          onMouseMove={(e) => control && send({ kind: "move", ...coords(e) })}
          onClick={(e) => control && send({ kind: "click", ...coords(e), button: "left" })}
          onContextMenu={(e) => { e.preventDefault(); control && send({ kind: "click", ...coords(e), button: "right" }); }}
          onDoubleClick={(e) => control && send({ kind: "click", ...coords(e), button: "left", double: true })}
          onWheel={(e) => control && send({ kind: "scroll", ...coords(e), dy: e.deltaY > 0 ? -1 : 1 })}
          style={{ width: "100%", cursor: control ? "crosshair" : "default", userSelect: "none" }} />
          : <div className="p-16 text-center text-zinc-500 text-sm">Waiting for first frame…</div>}
      </div>
    </div>
  );
}
